export interface TreatmentInfoType {
  intent: string;
  readyForCareDate: string;
  preferredTreatmentStartDate: string;
  startDateAdditionalInformation: string;
  treatingDepartment: string;
  pregnancy: string;
  delayCategory: string;
  previousRadioTreatment: string;
  previousCompositePlanRequired: string;
  previousTreatmentAt: string;
  previousTreatmentDate: string;
  patientStatus: string;
  inPatientAt: string;
  priority: string;
  cied: string;
  previousMdt: string;
  ciedPatientDependancy: string;
  ciedLinacRequired: string;
  ciedProtocolRequired: string;
  preTreatmentCiedCheckRequired: string;
  referralToGcCardiologist: string;
  previousTreatmentAdditionalInformation: string;
  previousTreatmentRetreatment: string;
  previousTreatmentRetreatmentType: string;
  otherCiedInstructions: string;
  cardioonc: string;
  endoscope: string;
  chemotherapy: string;
  chemoRegime: string;
  customChemoWhere: string;
  chemoWhere: string;
  chemoSequencing: string;
  chemoStartDate: string;
  chemoEndDate: string;
  hormonesRegime: string;
  hormones: string;
  hormonesStartDate: string;
  hormonesEndDate: string;
  consentStatus: string;
  pharmacyPrescription: string;
  pharmacyPrescriptionOther: string;
}

export const DEFAULT_TREATMENT_INFO: TreatmentInfoType = {
  intent: '',
  readyForCareDate: '',
  preferredTreatmentStartDate: '',
  startDateAdditionalInformation: '',
  treatingDepartment: '',
  pregnancy: '',
  delayCategory: '',
  previousRadioTreatment: '',
  previousCompositePlanRequired: '',
  previousTreatmentAt: '',
  previousTreatmentDate: '',
  patientStatus: '',
  inPatientAt: '',
  priority: '',
  cied: '',
  ciedPatientDependancy: '',
  ciedLinacRequired: '',
  ciedProtocolRequired: '',
  previousMdt: '',
  preTreatmentCiedCheckRequired: '',
  previousTreatmentAdditionalInformation: '',
  previousTreatmentRetreatment: '',
  previousTreatmentRetreatmentType: '',
  referralToGcCardiologist: '',
  otherCiedInstructions: '',
  cardioonc: '',
  endoscope: '',
  chemotherapy: '',
  chemoRegime: '',
  customChemoWhere: '',
  chemoWhere: '',
  chemoSequencing: '',
  chemoStartDate: '',
  chemoEndDate: '',
  hormonesRegime: '',
  hormones: '',
  hormonesStartDate: '',
  hormonesEndDate: '',
  consentStatus: '',
  pharmacyPrescription: '',
  pharmacyPrescriptionOther: '',
};

export interface ListOptionType {
  label: string;
  value: string;
}

export interface ListDataQueryType {
  carepathDataReferenceList: {
    data: {
      id: string;
      data: {
        option: string;
      };
      listWeight: string;
    };
  };
}

export const BOOLEAN_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

export const BOOLEAN_UNKNOWN_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'Unknown', value: 'Unknown' },
];

export const BOOLEAN_TBC_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'To be clarified', value: 'tbc' },
];

export const UK_CIED_OPTIONS = [
  { label: 'CIED', value: 'cied' },
  { label: 'ICD', value: 'icd' },
  { label: 'To be clarified', value: 'tbc' },
  { label: 'No', value: 'no' },
];

export const PATIENT_STATUS_OPTIONS = [
  { label: 'In Patient', value: 'inPatient' },
  { label: 'Out Patient', value: 'outPatient' },
  { label: 'Unknown', value: 'unknown' },
];

export const SCALE_OPTIONS = [
  { label: 'ECOG', value: 'ecog' },
  { label: 'KPS', value: 'kps' },
  { label: 'N/A', value: 'na' },
];

export const KPS_PERFORMANCE_STATUS_OPTIONS = [
  {
    value: '100 - Normal; no complaints; no evidence of disease',
    label: '100 - Normal; no complaints; no evidence of disease',
  },
  {
    value: '90 - Able to carry on normal activity; minor signs or symptoms of disease',
    label: '90 - Able to carry on normal activity; minor signs or symptoms of disease',
  },
  {
    value: '80 - Normal activity with effort; some signs or symptoms of disease',
    label: '80 - Normal activity with effort; some signs or symptoms of disease',
  },
  {
    value: '70 - Cares for self; unable to carry on normal activity or to do active work',
    label: '70 - Cares for self; unable to carry on normal activity or to do active work',
  },
  {
    value: '60 - Requires occasional assistance, but is able to care for most of his/her personal needs',
    label: '60 - Requires occasional assistance, but is able to care for most of his/her personal needs',
  },
  {
    value: '50 - Requires considerable assistance and frequent medical care',
    label: '50 - Requires considerable assistance and frequent medical care',
  },
  {
    value: '50 - Disabled; requires special care and assistance',
    label: '50 - Disabled; requires special care and assistance',
  },
  {
    value: '30 - Severely disabled; hospital admission is indicated although death not imminent',
    label: '30 - Severely disabled; hospital admission is indicated although death not imminent',
  },
  {
    value: '20 - Very sick; hospital admission necessary; active supportive treatment necessary',
    label: '20 - Very sick; hospital admission necessary; active supportive treatment necessary',
  },
  {
    value: '10 - Moribund; fatal processes progressing rapidly',
    label: '10 - Moribund; fatal processes progressing rapidly',
  },
  {
    value: '0 - Dead',
    label: '0 - Dead',
  },
];

export const KARNOFSKY_ABILITY_LEVELS = [
  {
    abilityLevel: 'Able to carry on normal activity and to work; no special care needed.',
    rowSpan: 3,
  },
  {
    abilityLevel:
      'Unable to work; able to live at home and care for most personal needs; varying amount of assistance needed.',
    rowSpan: 4,
  },
  {
    abilityLevel:
      'Unable to care for self; requires equivalent of institutional or hospital care; disease may be progressing rapidly.',
    rowSpan: 4,
  },
];

export const PROXIMITY_OPTIONS = [
  { label: 'Re-treatment', value: 'Re-treatment' },
  { label: 'Overlapping', value: 'Overlapping' },
  { label: 'Adjacent', value: 'Adjacent' },
  { label: 'Distant', value: 'Distant' },
  { label: 'Determine at Planning', value: 'Determine at Planning' },
];

export const ECOG_PERFORMANCE_STATUS_OPTIONS = [
  {
    value: '0 - Fully active, able to carry on all pre-disease performance without restriction',
    label: '0 - Fully active, able to carry on all pre-disease performance without restriction',
  },
  {
    value:
      '1 - Restricted in physically strenuous activity but ambulatory and able to carry out work of a light or sedentary nature',
    label:
      '1 - Restricted in physically strenuous activity but ambulatory and able to carry out work of a light or sedentary nature',
  },
  {
    value:
      '2 - Ambulatory and capable of all selfcare but unable to carry out any work activities. Up and about more than 50% of waking hours',
    label:
      '2 - Ambulatory and capable of all selfcare but unable to carry out any work activities. Up and about more than 50% of waking hours',
  },
  {
    value: '3 - Capable of only limited selfcare, confined to bed or chair more than 50% of waking hours',
    label: '3 - Capable of only limited selfcare, confined to bed or chair more than 50% of waking hours',
  },
  {
    value: '4 - Completely disabled. Cannot carry on any selfcare. Totally confined to bed or chair',
    label: '4 - Completely disabled. Cannot carry on any selfcare. Totally confined to bed or chair',
  },
  {
    value: '5 - Dead',
    label: '5 - Dead',
  },
];

export const BREADTH_HOLD_OPTIONS = [
  { label: 'Free Breathing', value: 'Free Breathing' },
  { label: 'DIBH', value: 'DIBH' },
  { label: 'EEBH', value: 'EEBH' },
  { label: 'Determined at CT', value: 'Determined at CT' },
];

export const DELAY_CATEGORY_OPTIONS = [
  {
    label: 'Category 1',
    value: 'Category 1',
    tooltip: {
      heading: 'Priority 1',
      list: [
        { item: 'SCC HN' },
        { item: 'Lung cancer (small and non-small cell)' },
        { item: 'Gynaecological SCC (cervix, vagina, vulva, etc.)' },
        { item: 'Oesophagus (SCC+adeno)' },
        { item: 'Cutaneous SCC' },
        { item: 'SCC anus' },
        { item: 'Meduloblastoma/PNET and other tumours with short doubling time' },
      ],
    },
  },
  {
    label: 'Category 2',
    value: 'Category 2',
    tooltip: {
      heading: 'Priority 2',
      body: 'Patients with the following tumours should not have their radical radiotherapy prolonged:',
      list: [
        {
          item: 'Squamous cell carcinoma of the head and neck region (grade B recommendation based on level 2 evidence)',
        },
        { item: 'Non-small cell lung carcinoma (NSCLC) - (grade C recommendation)' },
        { item: 'Squamous cell carcinoma of the cervix (grade D recommendation)' },
        { item: 'Small cell lung carcinoma (chemoradiotherapy) - (grade D recommendation)' },
        { item: 'Squamous cell carcinoma of oesophagus (grade D recommendation)' },
        { item: 'Squamous cell carcinoma of skin, vagina or vulva (grade D recommendation)' },
        { item: 'Squamous cell carcinoma of the anus (grade C recommendation)' },
        { item: 'Adenocarcinoma of the oesophagus (grade D recommendation)' },
        {
          item: 'Medulloblastoma and primitive neuroectodermal tumours (PNET) - (grade B recommendation based on level 2 evidence)',
        },
        {
          item: 'Patients with tumours with a short mass-doubling time (grade D recommendation based on level 4 evidence)',
        },
      ],
    },
  },
  {
    label: 'Category 3',
    value: 'Category 3',
    tooltip: {
      heading: 'Priority 3',
      body: 'Patients with the following receiving brachytherapy plus external beam therapy should not have the combined overall treatment time prolonged:',
      list: [
        { item: 'Squamous cell carcinoma of the cervix (grade B recommendation)' },
        { item: 'Squamous cell carcinoma of the tongue (grade C recommendation)' },
        { item: 'Squamous cell carcinoma of the anus (grade D recommendation)' },
      ],
    },
  },
];

export const RETREATMENT_OPTIONS = [
  { label: 'Partial', value: 'partial' },
  { label: 'Complete', value: 'complete' },
];
